import {
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Switch,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import CardForm from "components/ui/CardForm";
import FilterGroup from "components/ui/FilterGroup";
import FixedBar from "components/ui/FixedBar";
import FormControlGroup from "components/ui/FormControlGroup";
import {
  ResourceCreatableSelect,
  ResourceSelect,
} from "components/ui/ResourceCreatableSelect";
import SubmitButton from "components/ui/SubmitButton";
import SearchProvider from "contexts/SearchContext";
import { CONTENT_GROUP_TYPE_NAMES } from "features/content-groups/components/ContentGroupsList";
import { useSaveList } from "features/lists/hooks/useSaveList";
import { useMessages } from "hooks/useMessages";
import { ArrayRequiredTestFunction } from "lib/validation";
import {
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import * as Yup from "yup";
import SearchResultMembers from "../SearchResultMembers";
import RecipientsByCompanies from "./RecipientsByCompanies";

export interface FormValues {
  name: string;
  isNewsletter?: boolean;
  requiresReview?: boolean;
  tags: [];
  contentGroups: [];
  recipients: [];
}
interface Props {
  list: any;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  tags: Yup.mixed().test("tags", "", ArrayRequiredTestFunction),
  contentGroups: Yup.mixed().test(
    "contentGroups",
    "",
    ArrayRequiredTestFunction
  ),
  recipients: Yup.mixed().test("recipients", "", ArrayRequiredTestFunction),
});

export default function FormEditList({ list }: Props) {
  const methods = useForm<FormValues>({
    mode: "all",
    resolver: yupResolver(validationSchema) as unknown as Resolver<FormValues>,
    defaultValues: {
      name: list.name,
      isNewsletter: list.isNewsletter,
      requiresReview: list.requiresReview || false,
      tags: list.tags.map(({ id, name, colorScheme }) => ({
        value: id,
        label: name,
        colorScheme,
      })),
      contentGroups: list.contentGroups.map(({ id, name, type }) => ({
        value: id,
        label: `${CONTENT_GROUP_TYPE_NAMES[type]} - ${name}`,
      })),
      recipients: list.recipients.map(({ id }) => id),
    },
  });

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = methods;

  const isNewsletter = watch("isNewsletter");

  const { onError, onSuccess } = useMessages({
    success: "Dados da lista atualizados com sucesso!",
  });

  const { onSaveList, isPending } = useSaveList({
    listId: list.id,
    onSuccess,
    onError: (error) => onError(error?.response?.data?.message),
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => onSaveList(data);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Heading as="h1" size="h1">
          Dados da lista
          <span>Como a lista será identificada no sistema</span>
        </Heading>

        <CardForm mb="90px">
          <FormControlGroup>
            <FormControl isInvalid={!!errors.name} isRequired>
              <FormLabel>Nome</FormLabel>
              <Input {...register("name")} />
            </FormControl>
          </FormControlGroup>

          <FormControlGroup>
            <FormControl
              as={Flex}
              align="center"
              gap="10px"
              maxWidth="280px"
              isInvalid={!!errors.isNewsletter}
            >
              <Switch id="email-alerts" {...register("isNewsletter")} />
              <FormLabel htmlFor="email-alerts" mb="0">
                Lista de Newsletter
              </FormLabel>
            </FormControl>

            {isNewsletter && (
              <FormControl
                as={Flex}
                align="center"
                gap="10px"
                maxWidth="280px"
                isInvalid={!!errors.requiresReview}
              >
                <Switch id="requires-review" {...register("requiresReview")} />
                <FormLabel htmlFor="requires-review" mb="0">
                  Necessário revisar antes do envio
                </FormLabel>
              </FormControl>
            )}
          </FormControlGroup>

          <FormControlGroup>
            <FormControl isInvalid={!!errors.contentGroups} isRequired>
              <FormLabel>Operação</FormLabel>
              <ResourceSelect
                control={control}
                name="contentGroups"
                resourceKey="contentGroups"
                listPath="/content-groups"
                nameFormat={(record) =>
                  `${CONTENT_GROUP_TYPE_NAMES[record.type]} - ${record.name}`
                }
                isMulti
                placeholder="Selecione uma opção"
              />
            </FormControl>
            <FormControl isInvalid={!!errors.tags} isRequired>
              <FormLabel>Tags</FormLabel>
              <ResourceCreatableSelect
                control={control}
                name="tags"
                resourceKey="tags"
                listPath="/tags/LIST"
                createPath="/tags"
                createData={{ type: "LIST" }}
                isMulti
                placeholder="Selecione uma opção"
              />
            </FormControl>
          </FormControlGroup>

          <Divider my="35px" />

          <Heading as="h3" size="h3">
            Membros
          </Heading>
          <SearchProvider
            searchKey="recipients"
            searchPath="/recipients"
            searchMode="query"
            searchParams={{
              companyIds: [list.company.id],
            }}
            urlParamKey="membersQ"
          >
            <FilterGroup mt="20px" pos="relative">
              <SearchResultMembers />
            </FilterGroup>
          </SearchProvider>

          <RecipientsByCompanies companyIds={[list.company.id]} />
        </CardForm>

        <FixedBar>
          <SubmitButton
            isDisabled={isPending || !isValid}
            isLoading={isPending}
            hideIcon
          >
            Salvar
          </SubmitButton>
        </FixedBar>
      </form>
    </FormProvider>
  );
}
